import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarDays, Building2, Hotel, Users, QrCode, Plane, BedDouble,
  TrendingUp, ArrowRight, Crown, Bus,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import useFetch from '../../hooks/useFetch';
import useChartMotion from '../../hooks/useChartMotion';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import {
  Card,
  Stat,
  Loader,
  Button,
  StatusBadge,
  EmptyState,
  stagger,
  SectionTitle,
} from '../../components/UI';
import { fmtDate, fmtNumber, EVENT_TYPE_LABEL } from '../../utils/format';
export default function AdminDashboard() {
  const { user } = useAuth();
  const { events, loading: eventsLoading } = useEvents();
  const { data, loading } = useFetch('/reports/overview');
  const chartMotion = useChartMotion(1100);

  if (loading || eventsLoading) return <Loader full label="Loading your dashboard" />;

  const upcoming = events
    .filter((e) => new Date(e.endDate) >= new Date() && e.status !== 'cancelled')
    .slice(0, 4);

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      {/* ── greeting ─────────────────────────────────────────────── */}
      <Card padding={26} style={{ marginBottom: 22, overflow: 'hidden' }}>
        <motion.div
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: -90, right: -50, width: 320, height: 320,
            background: 'radial-gradient(circle, rgba(239,212,138,0.12), transparent 70%)',
            filter: 'blur(40px)', pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 12, color: 'var(--gold-500)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
              {greeting()}
            </p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 31, marginTop: 6, fontWeight: 600 }}>
              {user?.name}
            </h2>
            <p style={{ color: 'var(--text-mid)', fontSize: 13.5, marginTop: 6 }}>
              {data?.events.live > 0
                ? `${data.events.live} event${data.events.live === 1 ? ' is' : 's are'} live right now.`
                : `${data?.events.upcoming || 0} upcoming event${data?.events.upcoming === 1 ? '' : 's'} on your calendar.`}
            </p>
          </div>
          <Link to="/admin/events">
            <Button icon={CalendarDays}>Manage events</Button>
          </Link>
        </div>
      </Card>

      {/* ── headline stats ───────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: 14, marginBottom: 22 }}>
        <Stat label="Total Events" value={fmtNumber(data?.events.total)} icon={CalendarDays} tone="gold"
          sub={`${data?.events.upcoming || 0} upcoming · ${data?.events.live || 0} live`} />
        <Stat label="Participants" value={fmtNumber(data?.directory.participants)} icon={Users} tone="info"
          sub={`${data?.directory.vips || 0} VIP guests`} />
        <Stat label="Corporate Clients" value={fmtNumber(data?.directory.corporates)} icon={Building2} tone="success" />
        <Stat label="Partner Hotels" value={fmtNumber(data?.directory.hotels)} icon={Hotel} tone="warning" />
        <Stat label="Scans Today" value={fmtNumber(data?.activity.scansToday)} icon={QrCode} tone="gold"
          sub="Entry, sessions, boarding" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}
        className="gl-dash-grid">
        {/* ── growth chart ───────────────────────────────────────── */}
        <Card padding={22}>
          <SectionTitle
            title="Participant growth"
            subtitle="People onboarded to the platform each month"
          />
          {data?.trend?.length ? (
            <ResponsiveContainer width="100%" height={252}>
              <AreaChart data={data.trend} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="glGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EFD48A" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#EFD48A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#24242B" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ stroke: '#EFD48A', strokeOpacity: 0.25 }}
                  contentStyle={{
                    background: '#16161A', border: '1px solid #24242B',
                    borderRadius: 12, fontSize: 12, color: '#F2F2F5',
                  }}
                  labelStyle={{ color: '#EFD48A', fontWeight: 600, marginBottom: 4 }}
                />
                <Area type="monotone" dataKey="participants" stroke="#EFD48A" strokeWidth={2}
                  fill="url(#glGold)" {...chartMotion} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No participant history yet" icon={TrendingUp} />
          )}
        </Card>

        {/* ── upcoming events ────────────────────────────────────── */}
        <Card padding={22}>
          <SectionTitle title="Upcoming events" />
          {upcoming.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {upcoming.map((e, i) => (
                <motion.div
                  key={e._id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07 }}
                >
                  <Link to={`/admin/events/${e._id}`}>
                    <div style={{
                      padding: 14, border: '1px solid var(--border)',
                      borderRadius: 'var(--r)', background: 'var(--ink-800)',
                      transition: 'border-color 0.2s',
                    }}
                      onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = 'rgba(239,212,138,0.32)'; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 13.4, fontWeight: 600, lineHeight: 1.35 }}>{e.title}</p>
                          <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 5 }}>
                            {fmtDate(e.startDate)} · {e.venue?.city || EVENT_TYPE_LABEL[e.type]}
                          </p>
                        </div>
                        <StatusBadge status={e.status} />
                      </div>
                      <div style={{ display: 'flex', gap: 14, marginTop: 11, fontSize: 11.5, color: 'var(--text-mid)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Users size={12} /> {e.participantCount ?? 0}
                        </span>
                        {e.hotels?.length > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Hotel size={12} /> {e.hotels.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
              <Link to="/admin/events" style={{ marginTop: 4 }}>
                <Button variant="subtle" size="sm" style={{ width: '100%' }}>
                  View all events <ArrowRight size={13} />
                </Button>
              </Link>
            </div>
          ) : (
            <EmptyState
              message="No upcoming events"
              hint="Create an event to start planning travel, stay and sessions."
              icon={CalendarDays}
              action={<Link to="/admin/events"><Button size="sm">Create event</Button></Link>}
            />
          )}
        </Card>
      </div>

      {/* ── quick links ──────────────────────────────────────────── */}
      <div style={{ marginTop: 22 }}>
        <SectionTitle title="Jump to" subtitle="The modules you use most" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))', gap: 12 }}>
          {[
            { to: '/admin/participants', label: 'Participants', icon: Users, tone: 'info' },
            { to: '/admin/flights', label: 'Flights & Travel', icon: Plane, tone: 'gold' },
            { to: '/admin/rooms', label: 'Room Allocation', icon: BedDouble, tone: 'success' },
            { to: '/admin/transport', label: 'Bus & Transport', icon: Bus, tone: 'warning' },
            { to: '/admin/attendance', label: 'Attendance', icon: QrCode, tone: 'info' },
            { to: '/admin/registrations', label: 'Registrations', icon: Crown, tone: 'gold' },
          ].map((q, i) => (
            <motion.div key={q.to} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link to={q.to}>
                <Card hover padding={16} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 'var(--r-sm)',
                    background: 'rgba(239,212,138,0.11)', display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <q.icon size={16} color="var(--gold-400)" />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{q.label}</span>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 1000px) {
          .gl-dash-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </motion.div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
